// Shared inner content for KPI/status tiles: icon/label row + tone value + tone-dot hint, with
// an optional active/selected accent rail. Each call site (PerspectiveStatusTab, ConfigStatusTabs'
// StatusTab, OverviewSituationCards' SituationCard) supplies its own wrapper element (button/div)
// and composes this for the content — see those files for the `moduleCard` + `CardGlowOverlay`
// chrome around it. Two variants reproduce the two distinct layouts that already existed rather
// than forcing one visual shape onto both (#1520):
// - "tab" — small uppercase icon+label row above a 2xl value (the tab-bar shape).
// - "situation" — icon in a rounded box beside the label, a larger 3xl value (the situation-card
//   shape); the interactive-only "facet select" footer stays in SituationCard itself.
import type { LucideIcon } from "lucide-react";
import { cardToneText, cardToneDot, type CardTone } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

export type KpiStatusTileVariant = "tab" | "situation";

/** Rail + active-label color — "primary" for tab bars, "accent" for a situation-card facet
 *  selection (bkry convention: accent marks a *selection*, primary marks a *tab*). */
export type KpiStatusTileAccent = "primary" | "accent";

export type KpiStatusTileContentProps = {
  icon: LucideIcon;
  label: string;
  /** Large KPI — e.g. "12" or "3/5". Use "…" while loading. */
  value: string;
  tone: CardTone;
  hint: string;
  variant?: KpiStatusTileVariant;
  /** Draws the left accent rail (and, in the "tab" variant, tints the label). Omit for a tile
   *  that is never interactive. */
  active?: boolean;
  accent?: KpiStatusTileAccent;
};

export function KpiStatusTileContent({
  icon: Icon,
  label,
  value,
  tone,
  hint,
  variant = "tab",
  active = false,
  accent = "primary",
}: KpiStatusTileContentProps) {
  const accentRail = accent === "primary" ? "bg-primary" : "bg-accent";
  const activeRail = active && (
    <span
      aria-hidden
      className={cn("absolute inset-y-0 left-0 w-1 rounded-l-xl", accentRail)}
    />
  );

  if (variant === "situation") {
    return (
      <>
        {activeRail}
        <div className="relative flex items-center gap-2">
          <div
            className={cn(
              "h-7 w-7 rounded-md bg-surface-elevated grid place-items-center shrink-0",
              cardToneText[tone],
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </div>
          <span className="text-xs font-medium text-foreground/90 leading-tight min-w-0">
            {label}
          </span>
        </div>
        <div
          className={cn(
            "relative mt-4 font-display text-3xl tracking-tight tabular-nums",
            cardToneText[tone],
          )}
        >
          {value}
        </div>
        <div className="relative mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span
            className={cn("h-2 w-2 rounded-full shrink-0", cardToneDot[tone])}
          />
          <span className="min-w-0">{hint}</span>
        </div>
      </>
    );
  }

  const accentText =
    accent === "primary" ? "text-primary" : "text-accent-foreground";

  return (
    <>
      {activeRail}
      <div className="relative flex flex-col gap-2">
        <div
          className={cn(
            "flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em]",
            active ? accentText : "text-muted-foreground",
          )}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 truncate">{label}</span>
        </div>
        <div
          className={cn(
            "font-display text-2xl tracking-tight tabular-nums leading-none",
            cardToneText[tone],
          )}
        >
          {value}
        </div>
        <div className="flex items-center gap-2 text-[11px] leading-tight text-muted-foreground">
          <span
            className={cn("h-2 w-2 shrink-0 rounded-full", cardToneDot[tone])}
          />
          <span className="min-w-0">{hint}</span>
        </div>
      </div>
    </>
  );
}
